import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError, downloadFile, API } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../../components/ui/sheet";
import { Plus, MagnifyingGlass, PencilSimple, Trash, DownloadSimple, UploadSimple, Stack, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { pincode: "", locality: "", city: "", state: "", availability: {}, active_status: 1 };

export default function Serviceability() {
  const [lines, setLines] = useState([]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [linesOpen, setLinesOpen] = useState(false);
  const [lineForm, setLineForm] = useState({ label: "", category: "", order: 0 });
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const loadLines = useCallback(() => api.get("/service-lines").then((r) => setLines(r.data)), []);
  const load = useCallback(() => {
    api.get("/serviceable-pincodes", { params: { search } }).then((r) => setRows(r.data));
  }, [search]);
  useEffect(() => { loadLines(); }, [loadLines]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY, availability: {} }); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r, availability: { ...(r.availability || {}) } }); setEditId(r.id); setOpen(true); };

  const save = async () => {
    if (!/^\d{6}$/.test(form.pincode)) { toast.error("Enter a valid 6-digit pincode"); return; }
    try {
      const payload = { ...form, active_status: form.active_status ? 1 : 0 };
      if (editId) await api.put(`/serviceable-pincodes/${editId}`, payload);
      else await api.post("/serviceable-pincodes", payload);
      toast.success(`Pincode ${editId ? "updated" : "added"}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete pincode ${r.pincode}?`)) return;
    await api.delete(`/serviceable-pincodes/${r.id}`); toast.success("Deleted"); load();
  };

  const toggleAvail = async (r, slug) => {
    const next = { ...(r.availability || {}), [slug]: r.availability?.[slug] === 1 ? 0 : 1 };
    await api.put(`/serviceable-pincodes/${r.id}`, { ...r, availability: next });
    load();
  };

  const downloadTemplate = async () => {
    try { await downloadFile("/serviceable-pincodes/import-template", "serviceable_pincodes_template.csv"); }
    catch { toast.error("Could not download template"); }
  };
  const onImport = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/serviceable-pincodes/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${r.data.created} added, ${r.data.updated} updated`);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} row error(s)`);
      load();
    } catch (e2) { toast.error(apiError(e2.response?.data?.detail)); }
    setImporting(false);
  };

  const addLine = async () => {
    if (!lineForm.label) { toast.error("Service name required"); return; }
    try {
      await api.post("/service-lines", { label: lineForm.label, category: lineForm.category, order: Number(lineForm.order) || 0 });
      setLineForm({ label: "", category: "", order: 0 });
      toast.success("Service line added"); loadLines();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delLine = async (l) => {
    if (!window.confirm(`Delete service line "${l.label}"?`)) return;
    await api.delete(`/service-lines/${l.id}`); toast.success("Deleted"); loadLines();
  };

  return (
    <>
      <PageHeader title="Serviceability" subtitle="Manage service availability by pincode"
        actions={
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onImport} data-testid="sp-import-input" />
            <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={() => setLinesOpen(true)} data-testid="manage-lines-btn">
              <Stack size={15} weight="bold" /> Service Lines
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm gap-2" onClick={downloadTemplate} data-testid="sp-template-btn">
              <DownloadSimple size={15} weight="bold" /> Template
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm gap-2" disabled={importing} onClick={() => fileRef.current?.click()} data-testid="sp-import-btn">
              <UploadSimple size={15} weight="bold" /> {importing ? "Importing…" : "Import CSV"}
            </Button>
            <Button onClick={openNew} className="rounded-sm gap-2" data-testid="add-pincode-btn"><Plus size={16} weight="bold" /> Add Pincode</Button>
          </div>
        } />
      <div className="p-8 space-y-4">
        <div className="relative max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="sp-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pincode, locality, city…" className="rounded-sm pl-9" />
        </div>
        <div className="border border-border rounded-sm bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Pincode</TableHead><TableHead>Locality</TableHead>
                <TableHead>City</TableHead><TableHead>State</TableHead>
                {lines.map((l) => <TableHead key={l.slug} className="text-center text-[11px]">{l.label}</TableHead>)}
                <TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="sp-table">
              {rows.length === 0 && <TableRow><TableCell colSpan={6 + lines.length} className="text-center py-10 text-muted-foreground">No pincodes yet</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.id} className="hover:bg-secondary/30">
                  <TableCell className="font-mono font-semibold">{r.pincode}</TableCell>
                  <TableCell>{r.locality || "—"}</TableCell>
                  <TableCell>{r.city || "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.state || "—"}</TableCell>
                  {lines.map((l) => (
                    <TableCell key={l.slug} className="text-center">
                      <button onClick={() => toggleAvail(r, l.slug)} data-testid={`toggle-${r.pincode}-${l.slug}`}
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${r.availability?.[l.slug] === 1 ? "bg-green-100 text-green-700" : "bg-secondary text-muted-foreground"}`}>
                        {r.availability?.[l.slug] === 1 ? <Check size={14} weight="bold" /> : <X size={14} weight="bold" />}
                      </button>
                    </TableCell>
                  ))}
                  <TableCell>
                    <span className={`text-xs font-semibold ${r.active_status ? "text-green-700" : "text-muted-foreground"}`}>{r.active_status ? "Active" : "Off"}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`edit-pincode-${r.pincode}`}><PencilSimple size={16} /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(r)} data-testid={`del-pincode-${r.pincode}`}><Trash size={16} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="pincode-form">
          <SheetHeader><SheetTitle className="font-heading">{editId ? "Edit Pincode" : "Add Pincode"}</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <div><Label className="label-caps">Pincode</Label>
              <Input data-testid="sp-field-pincode" maxLength={6} value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, "") })} className="rounded-sm mt-1.5" /></div>
            <div><Label className="label-caps">Locality</Label>
              <Input data-testid="sp-field-locality" value={form.locality} onChange={(e) => setForm({ ...form, locality: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps">City</Label><Input data-testid="sp-field-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-sm mt-1.5" /></div>
              <div><Label className="label-caps">State</Label><Input data-testid="sp-field-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="rounded-sm mt-1.5" /></div>
            </div>
            <div>
              <Label className="label-caps mb-2 block">Service Availability</Label>
              <div className="space-y-2">
                {lines.map((l) => (
                  <label key={l.slug} className="flex items-center justify-between border border-border rounded-sm px-3 py-2 cursor-pointer">
                    <span className="text-sm">{l.label}</span>
                    <input type="checkbox" className="h-4 w-4 accent-primary" data-testid={`sp-avail-${l.slug}`}
                      checked={form.availability?.[l.slug] === 1}
                      onChange={(e) => setForm({ ...form, availability: { ...form.availability, [l.slug]: e.target.checked ? 1 : 0 } })} />
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={!!form.active_status} onChange={(e) => setForm({ ...form, active_status: e.target.checked ? 1 : 0 })} data-testid="sp-field-active" />
              <span className="text-sm">Active</span>
            </label>
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={save} data-testid="sp-save">Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={linesOpen} onOpenChange={setLinesOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="service-lines-sheet">
          <SheetHeader><SheetTitle className="font-heading">Service Lines</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <p className="text-sm text-muted-foreground">Add or remove services. New services automatically appear as columns and CSV fields.</p>
            <div className="space-y-2">
              {lines.map((l) => (
                <div key={l.slug} className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                  <div><div className="text-sm font-semibold">{l.label}</div><div className="text-xs text-muted-foreground">{l.slug}{l.category ? ` · ${l.category}` : ""}</div></div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => delLine(l)} data-testid={`del-line-${l.slug}`}><Trash size={15} /></Button>
                </div>
              ))}
            </div>
            <div className="border-t border-border pt-4 space-y-3">
              <div><Label className="label-caps">Service Name</Label><Input data-testid="line-label" value={lineForm.label} onChange={(e) => setLineForm({ ...lineForm, label: e.target.value })} placeholder="e.g. Geyser Repair" className="rounded-sm mt-1.5" /></div>
              <div><Label className="label-caps">Booking Category (optional)</Label><Input data-testid="line-category" value={lineForm.category} onChange={(e) => setLineForm({ ...lineForm, category: e.target.value })} placeholder="Maps to catalog category" className="rounded-sm mt-1.5" /></div>
              <div><Label className="label-caps">Order</Label><Input data-testid="line-order" type="number" value={lineForm.order} onChange={(e) => setLineForm({ ...lineForm, order: e.target.value })} className="rounded-sm mt-1.5" /></div>
              <Button className="rounded-sm w-full gap-2" onClick={addLine} data-testid="add-line-btn"><Plus size={15} /> Add Service Line</Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
